import { decodeHtml } from '~/js/utilities';
import {
  fetchProducts,
  productsData,
} from '~/js/utilities/products-and-collections';
import { getProductBadges } from '~/js/utilities/product';
import Swiper, { Pagination } from 'swiper';
import { formatPriceRange } from '~/js/utilities/products-and-collections';
import { getAlgoliaQueryId, trackClickedObjectAfterSearch, trackSearchAttribution } from '~/js/utilities/algolia';

const intersectionObserverCallbacks = new WeakMap();
const intersectionObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        intersectionObserverCallbacks.get(entry.target)?.();
      }
    });
  },
  { threshold: 0.3 }
);

const supportsHover = window.matchMedia('(hover: hover)');

const QUICK_ADD_BUTTON_ACTIONS = {
  QUICK_ADD: 'quick-add',
  NOTIFY_ME: 'notify-me',
  SOLD_OUT: 'sold-out',
};

const productCard = (
  productId,
  groupedProductIds,
  extraTextTemplate,
  algoliaData
) => ({
  loading: false,
  error: '',
  productId,
  imagesSwiper: null,
  groupedProducts: [],
  swatch_objects: [],
  isInsideCarousel: false,
  groupedProductIds,
  extraTextTemplate,
  activeSwatchPId: productId,
  isFirstRender: true,
  productCardTitle: null,
  productCardExtraText: null,
  productCardLinkWrapper: null,
  showSwatchLeftArrow: false,
  showSwatchRightArrow: true,
  swatchScrollPosition: 0,

  algoliaData: algoliaData,

  get shopifyData() {
    if (!this.productId) return null;
    if (this.loading) return null;
    return productsData[this.productId];
  },

  get activeSwatchData() {
    if (!this.swatch_objects) return null;
    if (!this.activeSwatchPId) return null;
    return productsData[this.activeSwatchPId];
  },

  get gwpFeatureEnable() {
    return window.GWP_FEATURE_ENABLE || false;
  },
  get gwpFeatured() {
    if (!this.shopifyData) return null;
    return this.shopifyData?.gwpProduct || null;
  },
  get gwpProductFeatureAvailable() {
    if (this.gwpFeatured) {
      return this.gwpFeatured?.reference?.availableForSale;
    }
    return false;
  },

  get link() {
    return `${window.Shopify.routes.root}products/${this.handle}`;
  },

  get handle() {
    return this.shopifyData?.handle || this.algoliaData?.handle;
  },

  get images() {
    let images = [];

    if (this.algoliaData?.image) {
      images.push({
        url: this.algoliaData.image,
        altText: this.algoliaData.title,
        width: 940,
        height: 940,
        id: 'algolia-image',
      });
    }

    if (this.shopifyData?.images?.nodes) {
      if (images.length === 0) {
        images = this.shopifyData?.images?.nodes?.slice(0, 2) || [];
      } else {
        images.push(this.shopifyData?.images?.nodes?.[1]);
      }
    }

    return images;
  },

  get badges() {
    return this.shopifyData ? getProductBadges(this.shopifyData) : [];
  },

  get qualifiersParsed() {
    const { qualifiers } = this.shopifyData;
    if (!qualifiers?.value) return '';
    try {
      return JSON.parse(qualifiers.value).join(' ');
    } catch (e) {
      return '';
    }
  },

  get metafieldPoweredTitle() {
    if (!this.shopifyData && !this.algoliaData) return false;

    const { print, description, type } = this.shopifyData;

    return Boolean(
      print?.value &&
        ((this.qualifiersParsed && type?.value) || description?.value)
    );
  },

  get superTitle() {
    const { brand, print } = this.shopifyData;

    const brandNormalized =
      brand?.value && !brand.value.includes('Posh Peanut') ? brand.value : '';

    return `${brandNormalized ? `${brandNormalized} ` : ''}${print?.value}`;
  },

  get title() {
    const { description } = this.shopifyData;
    if (description?.value) return description.value;

    return `${this.qualifiersParsed} ${this.shopifyData.type.value}`;
  },

  get algoliaSuperTitle() {
    if (!this.algoliaData) return null;
    const { meta } = this.algoliaData;
    const custom = meta?.custom || {};
    const { brand = '', print = '' } = custom;

    const brandNormalized =
      brand && !brand.includes('Posh Peanut') ? brand : '';

    return `${brandNormalized ? `${brandNormalized} ` : ''}${print}`;
  },

  get algoliaTitle() {
    if (!this.algoliaData) return null;
    const { meta } = this.algoliaData;
    const custom = meta?.custom || {};
    const { description = '' } = custom;

    if (description) return description;

    return null;
  },

  get legacyTitle() {
    return this.shopifyData?.title || this.algoliaData?.title;
  },

  get customPdpRedCopy() {
    const { customPdpRedCopy } = this.shopifyData;
    if (customPdpRedCopy?.value) return customPdpRedCopy.value;
    return null;
  },

  get customPlpRedCopy() {
    if (this.activeSwatchData) {
      if (this.activeSwatchData?.customPlpRedCopy?.value) {
        return this.activeSwatchData?.customPlpRedCopy?.value;
      }
      return null;
    }
    const { customPlpRedCopy } = this.shopifyData;
    if (customPlpRedCopy?.value) return customPlpRedCopy.value;
    return null;
  },

  get pdpCopyThemeState() {
    const { pdpCopyThemeState } = this.shopifyData;
    if (pdpCopyThemeState?.value) return pdpCopyThemeState.value;
    return null;
  },

  get plpCopyThemeState() {
    const { plpCopyThemeState } = this.shopifyData;
    if (plpCopyThemeState?.value) return plpCopyThemeState.value;
    return null;
  },

  get price() {
    if (this.activeSwatchData) {
      return this.activeSwatchData.priceRangeFormatted;
    }
    return this.shopifyData.priceRangeFormatted;
  },

  get priceRegular() {
    if (this.activeSwatchData) {
      return this.activeSwatchData.compareAtPriceRangeFormatted;
    }
    return this.shopifyData.compareAtPriceRangeFormatted;
  },

  get hasReducedPrice() {
    const { priceRegular, price } = this;
    return priceRegular && priceRegular !== price;
  },

  get isBundleProduct() {
    if (!this.shopifyData) return false;
    return !!this.shopifyData?.bundleProducts?.value;
  },

  get isAppExclusive() {
    const { tags } = this.shopifyData;
    return tags.includes('app-exclusive');
  },

  get pricingText() {
    return `reduced price: ${this.price}, regular price: ${this.priceRegular}`;
  },

  get pricingExtraText() {
    const { tags } = this.shopifyData;
    const tag = tags.find((tag) => tag.startsWith('Off:'));
    if (!tag) return '';
    const pct = tag.replace('Off:', '');
    return `${pct}%`;
  },

  get quickAddAction() {
    const { shopifyData, activeSwatchData } = this;
    const { availableForSale } = activeSwatchData || shopifyData;

    if (
      availableForSale &&
      !(
        shopifyData.tags.includes('vip-shop-early') &&
        window.VIP_SHOP_EARLY_STATE?.state &&
        !window.localStorage.getItem('isVIP')
      )
    ) {
      return QUICK_ADD_BUTTON_ACTIONS.QUICK_ADD;
    }

    if (
      (shopifyData.tags.includes('Coming Soon') ||
        shopifyData.tags.includes('will-restock') ||
        shopifyData.seasonalCore?.value === 'Core') &&
      (!shopifyData.tags.includes('vip-shop-early') ||
        !window.VIP_SHOP_EARLY_STATE?.state ||
        !window.localStorage.getItem('isVIP'))
    ) {
      return QUICK_ADD_BUTTON_ACTIONS.NOTIFY_ME;
    }

    if (
      !availableForSale &&
      shopifyData.tags.includes('will-restock') &&
      shopifyData.tags.includes('vip-shop-early') &&
      window.VIP_SHOP_EARLY_STATE?.state &&
      window.localStorage.getItem('isVIP')
    ) {
      return QUICK_ADD_BUTTON_ACTIONS.NOTIFY_ME;
    }

    return QUICK_ADD_BUTTON_ACTIONS.SOLD_OUT;
  },

  get quickAddTitle() {
    const titles = {
      [QUICK_ADD_BUTTON_ACTIONS.QUICK_ADD]: window.quickAddLabel || 'Quick Add',
      [QUICK_ADD_BUTTON_ACTIONS.NOTIFY_ME]: 'Notify Me',
      [QUICK_ADD_BUTTON_ACTIONS.SOLD_OUT]: 'Sold Out',
    };

    return (
      titles[this.quickAddAction] || titles[QUICK_ADD_BUTTON_ACTIONS.QUICK_ADD]
    );
  },

  get eventDetails() {
    return { productCard: this, handle: this.shopifyData?.handle };
  },

  get downloadAppTitle() {
    return 'Download App';
  },

  async init() {
    this.createOrDestroyCarousel = this.createOrDestroyCarousel.bind(this);
    supportsHover.addEventListener('change', this.createOrDestroyCarousel);
    this.isInsideCarousel = this.$el.closest('.swiper') !== null;

    if (!this.shopifyData) {
      this.loading = true;
      fetchProducts([this.productId])
        .then(() => {
          this.loading = false;
          this.ready();
        })
        .catch((e) => {
          this.error = `Error: ${e.message || 'Unknown error'}`;
        });
    } else {
      this.ready();
    }
    /*
    console.log(
      collectionId,collectionTitle,disable_extension,collectionDropRelease
    );
*/
  },

  async queryGroupedProducts() {
    try {
      const data = await fetchProducts(this.groupedProductIds);
      this.groupedProducts = this.groupedProductIds
        .map((id) => data[id])
        .filter(Boolean)
        .filter(
          (product) =>
            !product.tags.includes('SoldOut') && product.availableForSale
        );

      this.swatch_objects = Object.entries(this.groupedProducts).map(
        ([key, item]) => {
          const images = item.images.nodes || [];
          const lastImage = images.at(-1)?.url || '';
          const firstImage = images.at(0)?.url || '';
          const secondImage = images.at(1)?.url || '';

          return {
            index: key,
            name: decodeHtml(item.title || ''),
            swatch_image: lastImage,
            item_id: item.id.replace('gid://shopify/Product/', ''),
            product_url:
              item.onlineStoreUrl ||
              `${window.Shopify.routes.root}products/${item.handle}`,
            main_image: firstImage,
            hover_image: secondImage,
            title: item.description?.value || item.title,
            tags: item.tags,
            brand: item.brand?.value || '',
            print: item.print?.value || '',
          };
        }
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(e);
    }
  },

  destroy() {
    intersectionObserver.unobserve(this.$el);
    this.imagesSwiper?.destroy();
    supportsHover.removeEventListener('change', this.createOrDestroyCarousel);
  },

  ready() {
    this.$dispatch('product-card-render', this.eventDetails);

    if (!!this.shopifyData.groupedProducts?.value) {
      try {
        const ids = JSON.parse(this.shopifyData.groupedProducts.value);
        this.groupedProductIds = ids.map((id) =>
          id.replace('gid://shopify/Product/', '')
        );
        this.queryGroupedProducts();
      } catch (error) {
        console.log('Error loading swatches.');
      }
    }

    intersectionObserver.observe(this.$el);
    intersectionObserverCallbacks.set(
      this.$el,
      this.handleIntersection.bind(this)
    );

    this.createOrDestroyCarousel();
  },

  handleQuickAdd() {
    if (this.algoliaData) {
      trackSearchAttribution(
        this.algoliaData.__queryID,
        this.algoliaData.objectID,
        this.productId
      );

      trackClickedObjectAfterSearch({
        eventName: 'Hit Quick Add Clicked',
        objectIDs: [this.algoliaData.objectID],
        positions: [this.algoliaData.__position],
        queryID: getAlgoliaQueryId(),
      });
    }

    this.$dispatch('open-quick-add-drawer', {
      product: this.activeSwatchData || this.shopifyData,
      algoliaQueryID: getAlgoliaQueryId(),
    });
  },

  handleClick() {
    if (this.algoliaData) {
      trackSearchAttribution(
        getAlgoliaQueryId(),
        this.algoliaData.objectID,
        this.productId
      );

      trackClickedObjectAfterSearch({
        eventName: 'Hit Clicked',
        objectIDs: [this.algoliaData.objectID],
        positions: [this.algoliaData.position],
        queryID: getAlgoliaQueryId(),
      });
    }
    this.$dispatch('product-card-click', this.eventDetails);
  },

  swatchScrollToLeft() {
    if (!this.$refs.productCardPrints) return;
    this.$refs.productCardPrints.scrollLeft -=
      this.$refs.productCardPrints.offsetWidth / 5;
    this.showSwatchRightArrow = true;
    this.swatchScrollPosition = this.$refs.productCardPrints.scrollLeft;

    setTimeout(() => {
      if (this.$refs.productCardPrints.scrollLeft == 0) {
        this.showSwatchLeftArrow = false;
      }
    }, 300);
  },

  swatchScrollToRight() {
    if (!this.$refs.productCardPrints) return;

    if (
      this.$refs.productCardPrints.scrollLeft +
        this.$refs.productCardPrints.offsetWidth >=
      this.$refs.productCardPrints.scrollWidth - 1
    ) {
      this.$refs.productCardPrints.scrollLeft = 0;
      return;
    }

    this.$refs.productCardPrints.scrollLeft +=
      this.$refs.productCardPrints.offsetWidth / 5;
  },

  handleColorSwatchSelect(product_id) {
    let index = this.swatch_objects.findIndex(
      (swatch) => swatch.item_id === product_id
    );
    let selected_swatch_object = this.swatch_objects[index];
    const { brand, print, tags, main_image, hover_image, title, product_url } =
      selected_swatch_object;

    if (selected_swatch_object) {
      const mainImageElement = this.$el
        .closest('.product-card')
        .querySelectorAll('.product-card__swiper-slide img')[0];
      const hoverImageElement = this.$el
        .closest('.product-card')
        .querySelectorAll('.product-card__swiper-slide img')[1];
      this.activeSwatchPId = product_id;
      this.isFirstRender = false;

      if (mainImageElement) {
        mainImageElement.src = main_image;
        mainImageElement.srcset = main_image;
      }
      if (hoverImageElement) {
        mainImageElement.src = hover_image;
        hoverImageElement.srcset = hover_image;
      }

      if (this.$refs.productCardTitle)
        this.$refs.productCardTitle.textContent = title;

      const brandNormalized =
        brand && !brand.includes('Posh Peanut') ? brand : '';
      const superTitle = `${brandNormalized ? `${brandNormalized} ` : ''}${
        print ? print : ''
      }`;

      if (this.$refs.productCardSuperTitle)
        this.$refs.productCardSuperTitle.textContent = superTitle;
      if (this.$refs.productCardTopLinkWrapper && !!product_url)
        this.$refs.productCardTopLinkWrapper.href = product_url;
      if (this.$refs.productCardBottomLinkWrapper && !!product_url)
        this.$refs.productCardBottomLinkWrapper.href = product_url;

      if (this.$refs.productCardExtraText && this.extraTextTemplate) {
        const tag = tags.find((tag) => tag.startsWith('off:'));

        if (tag) {
          const pct = tag.replace('off:', '');
          this.$refs.productCardExtraText.textContent =
            this.extraTextTemplate.replace('{amount}', `${pct}%`);
        } else {
          this.$refs.productCardExtraText.textContent = '';
        }
      }

      if (productsData[product_id]) {
        const productData = productsData[product_id];
        this.handleColorSwatchGWPUpdate(productData, selected_swatch_object);
      } else {
        fetchProducts([product_id])
          .then((data) => {
            let productClicked = data[product_id];
            this.handleColorSwatchGWPUpdate(
              productClicked,
              selected_swatch_object
            );
          })
          .catch((e) => {
            this.error = `Error: ${e.message || 'Unknown error'}`;
          });
      }
    }
  },

  handleColorSwatchPriceUpdate(productData, selected_swatch_object) {
    const { tags } = selected_swatch_object;
    const minPrice = productData.priceRange.minVariantPrice;
    const maxPrice = productData.priceRange.maxVariantPrice;

    const tagDiscount = tags.find((tag) => tag.startsWith('salemd'));
    const regularPrice = this.$el
      .closest('.product-card')
      .querySelectorAll('.product-card__price.product-card__price--regular')[0];
    const currentPrice = this.$el
      .closest('.product-card')
      .querySelectorAll('.product-card__price.product-card__price--current')[0];
    const priceContainer = this.$el
      .closest('.product-card')
      .querySelectorAll('.product-card__pricing')[0];

    if (regularPrice && currentPrice) {
      const priceRegular = productData.compareAtPriceRangeFormatted;
      if (tagDiscount) {
        currentPrice.style.display = 'block';
        currentPrice.textContent = formatPriceRange(minPrice, maxPrice, true);
        regularPrice.textContent = priceRegular;
        regularPrice.classList.remove('product-card__price--regular--hidden');
      } else {
        currentPrice.textContent = '';
        currentPrice.style.display = 'none';
        regularPrice.textContent = formatPriceRange(minPrice, maxPrice, true);
        regularPrice.classList.add('product-card__price--regular--hidden');
      }
    } else {
      if (tagDiscount) {
        currentPrice.style.display = 'block';
        currentPrice.textContent = formatPriceRange(minPrice, maxPrice, true);
        const newElement = document.createElement('span');
        newElement.classList.add('product-card__price');
        newElement.classList.add('product-card__price--regular');
        if (!priceContainer.querySelector('.product-card__price--regular')) {
          const priceRegular = productData.compareAtPriceRangeFormatted;
          newElement.textContent = priceRegular;
          if (
            priceContainer.querySelector(
              '.product-card__price.product-card__price--current'
            )
          ) {
            priceContainer.appendChild(newElement);
          } else {
            if (window.GWP_FEATURE_ENABLE) {
              let card = this.$el.closest('.product-card');
              let gwpSpacer = card.querySelectorAll(
                '.product-card__gwp-spacer'
              );
              if (gwpSpacer && gwpSpacer.length > 0) {
                priceContainer.insertBefore(newElement, gwpSpacer[0]);
              } else {
                priceContainer.append(newElement);
              }
            } else {
              priceContainer.append(newElement);
            }
          }
        }
      } else {
        currentPrice.textContent = '';
        currentPrice.textContent = formatPriceRange(minPrice, maxPrice, true);

        if (priceContainer.querySelector('.product-card__price--regular')) {
          priceContainer
            .querySelector('.product-card__price--regular')
            .remove();
        }
      }
    }
  },

  handleColorSwatchGWPUpdate(productData, selected_swatch_object) {
    let card = this.$el.closest('.product-card');
    if (productData.gwpProduct == null || !window.GWP_FEATURE_ENABLE) {
      if (card) {
        let gwpSpacer = card.querySelectorAll('.product-card__gwp-spacer');
        if (gwpSpacer && gwpSpacer.length > 0) {
          gwpSpacer[0].style.display = 'none';
        }
      }
    } else {
      let gwpSpacer = card.querySelectorAll('.product-card__gwp-spacer');
      if (gwpSpacer && gwpSpacer.length > 0) {
        gwpSpacer[0].style.display = 'block';
      } else {
        console.log('create here the element');
        const freeGiftBadge = document.createElement('div');
        freeGiftBadge.classList.add('product-card__gwp-spacer');

        const freeGiftText = document.createElement('p');
        freeGiftText.textContent = 'FREE GIFT*';

        freeGiftText.classList.add('product-card__gwp-badge');
        freeGiftBadge.appendChild(freeGiftText);

        let cardPricing = card.querySelectorAll('.product-card__pricing')[0];
        cardPricing.appendChild(freeGiftBadge);
      }
    }
  },

  handleDownlodApp() {
    window.open('https://wn4uoivx.tapc.art');
  },

  handleIntersection() {
    this.$dispatch('product-card-intersection', this.eventDetails);
  },

  createOrDestroyCarousel() {
    this.imagesSwiper?.destroy();
    this.imagesSwiper = null;

    if (supportsHover.matches) return;
    if (this.images.length === 0) return;
    if (this.isInsideCarousel) return;

    this.$nextTick(() => {
      this.imagesSwiper = new Swiper(this.$refs.imagesSwiper, {
        modules: [Pagination],
        pagination: {
          el: '.swiper-pagination',
        },
      });
    });
  },
});

export default productCard;
